import { useEffect, useState } from "react";
import {
    Tooltip,
    Snackbar,
    Alert,
    Menu,
    MenuItem,
} from "@mui/material";
import ShareIcon from "@mui/icons-material/Share";
import { styled } from "@mui/system";
import * as htmlToImage from "dom-to-image-more";

const GlowingButton = styled("button")({
    background: "#2f527a",
    color: "#fff",
    border: "none",
    padding: "4px 6px",
    borderRadius: "6px",
    display: "flex",
    alignItems: "center",
    gap: "2px",
    fontSize: "0.75rem",
    cursor: "pointer",
    boxShadow: "0 0 6px rgba(47, 82, 122, 0.5)",
    transition: "all 0.3s ease",
    "&:hover": {
        transform: "scale(1.05)",
        boxShadow: "0 0 12px rgba(47, 82, 122, 0.8)",
    },
});

const ShareButton = ({ sharetitle, sharetext, sharemedia, screenshotRef }) => {



    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });
    const [anchorEl, setAnchorEl] = useState(null);
    const [canShareText, setCanShareText] = useState(false);
    const [canShareFiles, setCanShareFiles] = useState(false);

    useEffect(() => {
        setCanShareText(!!navigator.share);
        const file = new File([""], "test.txt", { type: "text/plain" });
        setCanShareFiles(!!navigator.canShare && navigator.canShare({ files: [file] }));
    }, []);

    const showToast = (message, severity = "info") => {
        setSnackbar({ open: true, message, severity });
    };

    const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

    const title = sharetitle || "No Subject";
    const rawMessage = sharetext || "No Description Added!";
    const cleanText = `${title}\n\n${rawMessage.replace(/<[^>]*>/g, "").trim()}`;
    const media = sharemedia || [];

    const handleMenuClick = (event) => setAnchorEl(event.currentTarget);
    const handleMenuClose = () => setAnchorEl(null);

    const handleShareText = async () => {
        handleMenuClose();
        try {
            await navigator.share({ title, text: cleanText });
            showToast("✅ Text shared successfully!", "success");
        } catch (err) {
            console.error("Text sharing failed:", err);
            showToast("❌ Failed to share text.", "error");
        }
    };

    const handleShareFiles = async () => {
        handleMenuClose();
        const files = [];

        for (let i = 0; i < media.length; i++) {
            const mediaItem = media[i];
            try {
                const response = await fetch(mediaItem.surl);
                if (!response.ok) throw new Error("Fetch failed");

                const blob = await response.blob();
                const ext = mediaItem.surl.split(".").pop().split("?")[0];
                const file = new File([blob], `file${i + 1}.${ext}`, { type: blob.type });

                if (navigator.canShare({ files: [file] })) {
                    files.push(file);
                } else {
                    console.warn(`Skipping unsupported file ${i + 1}`);
                }
            } catch (error) {
                console.warn(`Skipping failed file ${i + 1}:`, error);
                continue; // Skip to the next file
            }
        }

        if (files.length === 0) {
            showToast("❌ No valid files to share.", "error");
            return;
        }

        try {
            await navigator.share({ title, text: cleanText, files });
            showToast("✅ Files shared successfully!", "success");
        } catch (error) {
            console.error("Sharing failed:", error);
            showToast("❌ Sharing failed or cancelled.", "error");
        }
    };

    const handleShareScreenshot = async () => {
        handleMenuClose();
        const node = screenshotRef?.current;
        if (!node) {
            showToast("❌ Screenshot target not found.", "error");
            return;
        }

        try {
            const blob = await htmlToImage.toBlob(node, {
                useCORS: true,
                cacheBust: true,
                backgroundColor: "#ffffff",
            });

            const file = new File([blob], "screenshot.png", { type: "image/png" });

            if (navigator.canShare({ files: [file] })) {
                await navigator.share({ files: [file], title, text: cleanText });
                showToast("✅ Screenshot shared successfully!", "success");
            } else {
                showToast("❌ Unable to share screenshot.", "error");
            }
        } catch (error) {
            console.error("Screenshot sharing failed:", error);
            showToast("❌ Screenshot sharing failed.", "error");
        }
    };

    if (!canShareText && !canShareFiles) return null;

    return (
        <>
            <Tooltip title="Share post" arrow>
                <GlowingButton onClick={handleMenuClick}>
                    <ShareIcon sx={{ fontSize: "18px" }} />
                </GlowingButton>
            </Tooltip>

            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
                {canShareText && <MenuItem onClick={handleShareText}>📄 Text</MenuItem>}
                {canShareFiles && <MenuItem onClick={handleShareFiles}>📎 Files</MenuItem>}
                {canShareFiles && <MenuItem onClick={handleShareScreenshot}>📸 Screenshot</MenuItem>}
            </Menu>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={2000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert severity={snackbar.severity} onClose={handleCloseSnackbar} sx={{ width: "100%" }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default ShareButton;